import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { forkJoin, Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LocalDate } from '../../../core/model/local-date';
import { Money } from '../../../core/model/money';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { Article } from '../../../rent/model/article';
import { LeaseAgreementCategory, LeaseAgreementStatus } from '../../../rent/model/const/enums';
import { ArticleService } from '../../../rent/service/data/article.service';
import { ContactService } from '../../../rent/service/data/contact.service';
import { InvoiceDeliveryMethod } from '../../model/const/enum';
import {
  AmortizationPlanCreateDefaults,
  AmortizationPlanCreatePreview,
  AmortizationPlanCreateRequest,
  AmortizationPlanInstallmentEdit,
  AmortizationPlanLeaseAgreementOption,
  AmortizationPlanRentPeriodOption,
} from '../../model/amortization-plan-create';
import { AmortizationPlanService } from '../../service/data/amortization-plan.service';

@Component({
  standalone: false,
  selector: 'app-create-amortization-plan-sidebar',
  templateUrl: './create-amortization-plan-sidebar.component.html',
  styleUrls: ['./create-amortization-plan-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CreateAmortizationPlanSidebarComponent implements OnChanges, OnDestroy {
  constructor(
    private readonly _amortizationPlanService: AmortizationPlanService,
    private readonly _articleService: ArticleService,
    public readonly contactFilterService: ContactService,
    private readonly _messageService: MessageService,
    private readonly _fb: FormBuilder,
    private readonly _fcf: FormControlExFactory
  ) {
    this.form = this._fb.group({
      contactId: this._fcf.create('AMORTIZATION_PLAN.CREATE.RECIPIENT', null),
      articleId: this._fcf.createMandatory('AMORTIZATION_PLAN.CREATE.ARTICLE', ''),
      interestRate: this._fcf.createWithOptions('AMORTIZATION_PLAN.INTEREST_RATE', 0, { minValue: 0, required: true }),
      leaseAgreementId: this._fcf.create('AMORTIZATION_PLAN.CREATE.LEASE_AGREEMENT', ''),
      firstDueDate: this._fcf.createMandatory('AMORTIZATION_PLAN.CREATE.FIRST_DUE_DATE', this.getDefaultFirstDueDate()),
      firstRentPeriodId: this._fcf.create('AMORTIZATION_PLAN.CREATE.FIRST_RENT_PERIOD', ''),
      paymentCount: this._fcf.createWithOptions('AMORTIZATION_PLAN.CREATE.PAYMENT_COUNT', 1, { minValue: 1, required: true }),
      interestInvoiceDelivery: this._fb.group({
        deliveryMethod: this._fcf.create('INVOICE.DELIVERY_METHOD', null),
        email: this._fcf.createEmail('COMMON.EMAIL', null),
        eInvoiceAddress: this._fcf.create('TENANT.EINVOICE_ADDRESS', null),
        address: this._fb.group({
          name: this._fcf.createWithOptions('ADDRESS_FORM.RECIPIENT_LABEL', null, { length: 100 }),
          additionalName: this._fcf.createMaxLength('ADDRESS_FORM.ADDRESS_ROW2', null, 50),
          street: this._fcf.createWithOptions('ADDRESS_FORM.STREET_LABEL', null, { length: 100 }),
          postalCode: this._fcf.createWithOptions('ADDRESS_FORM.POSTAL_CODE_LABEL', null, { length: 20 }),
          city: this._fcf.createWithOptions('ADDRESS_FORM.CITY_LABEL', null, { length: 50 }),
          country: this._fcf.createWithOptions('ADDRESS_FORM.COUNTRY_LABEL', null, { length: 50 }),
        }),
      }),
      note: this._fcf.createMaxLength('COMMON.NOTE', '', 500),
    });

    this.form.controls['leaseAgreementId'].valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.updateLeaseAgreementState();
      });

    this.interestInvoiceDeliveryGroup.controls['deliveryMethod'].valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.updateInterestInvoiceDeliveryState();
      });
  }

  @Input() invoiceIds: string[] = [];

  @Input() selectContactFirst = false;

  @Output() created = new EventEmitter<void>();

  @Output() canceled = new EventEmitter<void>();

  readonly form: FormGroup;

  preview: AmortizationPlanCreatePreview;
  defaults: AmortizationPlanCreateDefaults;
  articleSelection: SelectionItem<string>[] = [];
  leaseAgreementSelection: SelectionItem<string>[] = [];
  firstRentPeriodSelection: SelectionItem<string>[] = [];
  isLoading = false;
  isSaving = false;
  private readonly _destroy$ = new Subject<void>();

  get selectedEligibleInvoices() {
    return (this.preview?.eligibleInvoices ?? []).filter(invoice => invoice.selected);
  }

  get showContactSelection(): boolean {
    return this.selectContactFirst && !this.preview && !this.isLoading;
  }

  get selectedContactId(): string | null {
    return this.form.controls['contactId'].value || null;
  }

  get selectedArticleId(): string | null {
    return this.form.controls['articleId'].value || null;
  }

  get selectedTotalAmount(): Money {
    const currencyCode = this.preview?.totalAmount?.currencyCode ?? 'SEK';
    const amount = this.selectedEligibleInvoices.reduce((sum, invoice) => sum + (invoice.unpaidAmount?.amount ?? 0), 0);
    return new Money(currencyCode, amount);
  }

  get availableLeaseAgreements(): AmortizationPlanLeaseAgreementOption[] {
    const leaseAgreements = this.defaults?.leaseAgreements ?? [];
    return leaseAgreements.filter(leaseAgreement =>
      (!this.preview?.companyId || leaseAgreement.companyId === this.preview.companyId)
      && (!this.preview?.debtorContactId || leaseAgreement.payerContactId === this.preview.debtorContactId)
    );
  }

  get selectedLeaseAgreement(): AmortizationPlanLeaseAgreementOption | null {
    const leaseAgreementId = this.form.controls['leaseAgreementId'].value;
    return this.availableLeaseAgreements.find(item => item.id === leaseAgreementId) ?? null;
  }

  get availableFirstRentPeriods(): AmortizationPlanRentPeriodOption[] {
    return this.selectedLeaseAgreement?.firstRentPeriods ?? [];
  }

  get selectedFirstRentPeriod(): AmortizationPlanRentPeriodOption | null {
    const firstRentPeriodId = this.form.controls['firstRentPeriodId'].value;
    return this.availableFirstRentPeriods.find(item => item.id === firstRentPeriodId) ?? null;
  }

  get interestInvoiceDeliveryGroup(): FormGroup {
    return this.form.controls['interestInvoiceDelivery'] as FormGroup;
  }

  get interestInvoiceDeliveryAddressGroup(): FormGroup {
    return this.interestInvoiceDeliveryGroup.controls['address'] as FormGroup;
  }

  get interestInvoiceDeliveryAddressModel(): any {
    return this.preview?.interestInvoiceDelivery?.address ?? {
      name: null,
      additionalName: null,
      street: null,
      postalCode: null,
      city: null,
      country: 'SE',
    };
  }

  get showCustomDeliveryConfiguration(): boolean {
    return !this.selectedLeaseAgreement;
  }

  get selectedInterestInvoiceDeliveryMethod(): InvoiceDeliveryMethod | null {
    const value = this.interestInvoiceDeliveryGroup.controls['deliveryMethod'].value;
    return typeof value === 'number' ? value : null;
  }

  get showInterestInvoiceEmail(): boolean {
    return this.selectedInterestInvoiceDeliveryMethod === InvoiceDeliveryMethod.EmailWithPDF
      || this.selectedInterestInvoiceDeliveryMethod === InvoiceDeliveryMethod.EmailWithoutPDF;
  }

  get showInterestInvoiceEInvoiceAddress(): boolean {
    return this.selectedInterestInvoiceDeliveryMethod === InvoiceDeliveryMethod.EInvoiceB2B;
  }

  get showInterestInvoicePostalAddress(): boolean {
    return this.selectedInterestInvoiceDeliveryMethod === InvoiceDeliveryMethod.Mail;
  }

  get disableSubmit(): boolean {
    return (
      this.isLoading ||
      this.isSaving ||
      !this.preview?.canCreate ||
      !this.selectedEligibleInvoices.length ||
      this.form.invalid
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectContactFirst']) {
      this.resetContext();
    }

    if (changes['invoiceIds'] && !this.selectContactFirst) {
      this.loadContext();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  cancel(): void {
    this.canceled.emit();
  }

  loadInvoicesForSelectedContact(): void {
    if (!this.selectedContactId || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.form.disable({ emitEvent: false });
    this._amortizationPlanService.getCreateInvoiceIdsForContact(this.selectedContactId).subscribe({
      next: invoiceIds => this.loadContext(invoiceIds ?? [], true),
      error: error => {
        this.isLoading = false;
        this.form.enable({ emitEvent: false });
        this._messageService.showError(error);
      },
    });
  }

  toggleInvoiceSelection(invoiceId: string, selected: boolean): void {
    const invoice = this.preview?.eligibleInvoices?.find(item => item.invoiceId === invoiceId);
    if (invoice) {
      invoice.selected = selected;
    }
  }

  submit(): void {
    if (this.disableSubmit) {
      return;
    }

    this.isSaving = true;
    this.form.disable({ emitEvent: false });

    this._amortizationPlanService.createFromInvoices(this.toCreateRequest()).subscribe({
      next: () => {
        this.isSaving = false;
        this.created.emit();
      },
      error: error => {
        this.isSaving = false;
        this.form.enable({ emitEvent: false });
        this.updateLeaseAgreementState();
        this._messageService.showError(error);
      },
    });
  }

  private loadContext(invoiceIds: string[] = this.invoiceIds, allowEmpty = false): void {
    const selectedInvoiceIds = (invoiceIds ?? []).filter(id => !!id);
    this.resetContext();

    if (!selectedInvoiceIds.length && !allowEmpty) {
      return;
    }

    this.isLoading = true;
    this.form.disable({ emitEvent: false });

    forkJoin({
      preview: this._amortizationPlanService.getCreatePreview({
        invoiceIds: selectedInvoiceIds,
        leaseAgreementId: null,
        separateInvoicing: false,
        firstRentPeriodId: null,
      }).pipe(take(1)),
      defaults: this._amortizationPlanService.getCreateDefaults().pipe(take(1)),
      articles: this._articleService.getList().pipe(take(1)),
    }).pipe(finalize(() => {
      this.isLoading = false;
    })).subscribe({
      next: ({ preview, defaults, articles }) => {
        try {
          this.preview = preview;
          this.defaults = defaults;
          this.articleSelection = this.toArticleSelection(articles, preview);
          this.leaseAgreementSelection = this.toLeaseAgreementSelection();
          this.resetForm();
        } catch (error) {
          this.resetContext();
          this.form.enable({ emitEvent: false });
          this._messageService.showError(error);
        }
      },
      error: error => {
        this.form.enable({ emitEvent: false });
        this._messageService.showError(error);
      },
    });
  }

  private resetContext(): void {
    this.preview = null;
    this.defaults = null;
    this.articleSelection = [];
    this.leaseAgreementSelection = [];
    this.firstRentPeriodSelection = [];
  }

  private resetForm(): void {
    for (const invoice of this.preview?.eligibleInvoices ?? []) {
      invoice.selected = true;
    }

    this.form.reset(
      {
        contactId: null,
        articleId: this.resolveDefaultArticleId(),
        interestRate: 0,
        leaseAgreementId: this.resolveDefaultLeaseAgreementId(),
        firstDueDate: this.getDefaultFirstDueDate(),
        firstRentPeriodId: '',
        paymentCount: 1,
        interestInvoiceDelivery: {
          deliveryMethod: this.preview?.interestInvoiceDelivery?.deliveryMethod ?? null,
          email: this.preview?.interestInvoiceDelivery?.email ?? null,
          eInvoiceAddress: this.preview?.interestInvoiceDelivery?.eInvoiceAddress ?? null,
          address: {
            name: this.preview?.interestInvoiceDelivery?.address?.name ?? null,
            additionalName: this.preview?.interestInvoiceDelivery?.address?.additionalName ?? null,
            street: this.preview?.interestInvoiceDelivery?.address?.street ?? null,
            postalCode: this.preview?.interestInvoiceDelivery?.address?.postalCode ?? null,
            city: this.preview?.interestInvoiceDelivery?.address?.city ?? null,
            country: this.preview?.interestInvoiceDelivery?.address?.country ?? null,
          },
        },
        note: '',
      },
      { emitEvent: false }
    );

    this.form.enable({ emitEvent: false });
    this.updateLeaseAgreementState();
    this.updateInterestInvoiceDeliveryState();
  }

  private updateLeaseAgreementState(): void {
    this.leaseAgreementSelection = this.toLeaseAgreementSelection();
    this.firstRentPeriodSelection = this.toFirstRentPeriodSelection();

    const leaseAgreementControl = this.form.controls['leaseAgreementId'];
    const firstDueDateControl = this.form.controls['firstDueDate'];
    const firstRentPeriodControl = this.form.controls['firstRentPeriodId'];

    const validLeaseAgreementIds = this.availableLeaseAgreements.map(item => item.id);
    if (leaseAgreementControl.value && !validLeaseAgreementIds.includes(leaseAgreementControl.value)) {
      leaseAgreementControl.setValue('', { emitEvent: false });
    }

    if (this.selectedLeaseAgreement) {
      firstRentPeriodControl.setValidators([Validators.required]);
      firstRentPeriodControl.enable({ emitEvent: false });

      const validFirstRentPeriodIds = this.availableFirstRentPeriods.map(item => item.id);
      const currentFirstRentPeriodId = firstRentPeriodControl.value;
      if (!validFirstRentPeriodIds.includes(currentFirstRentPeriodId)) {
        firstRentPeriodControl.setValue(validFirstRentPeriodIds[0] ?? '', { emitEvent: false });
      }

      firstDueDateControl.clearValidators();
      firstDueDateControl.disable({ emitEvent: false });
    } else {
      firstRentPeriodControl.clearValidators();
      firstRentPeriodControl.setValue('', { emitEvent: false });
      firstRentPeriodControl.disable({ emitEvent: false });

      firstDueDateControl.setValidators([Validators.required]);
      if (!LocalDate.fromDate(firstDueDateControl.value)?.hasValue()) {
        firstDueDateControl.setValue(this.getDefaultFirstDueDate(), { emitEvent: false });
      }

      firstDueDateControl.enable({ emitEvent: false });
    }

    firstDueDateControl.updateValueAndValidity({ emitEvent: false });
    firstRentPeriodControl.updateValueAndValidity({ emitEvent: false });
    this.updateInterestInvoiceDeliveryState();
  }

  private updateInterestInvoiceDeliveryState(): void {
    const deliveryGroup = this.interestInvoiceDeliveryGroup;
    const emailControl = deliveryGroup.controls['email'];
    const eInvoiceAddressControl = deliveryGroup.controls['eInvoiceAddress'];
    const addressGroup = this.interestInvoiceDeliveryAddressGroup;
    const requireCustomDelivery = this.showCustomDeliveryConfiguration;

    if (!requireCustomDelivery) {
      deliveryGroup.controls['deliveryMethod'].clearValidators();
      emailControl.clearValidators();
      eInvoiceAddressControl.clearValidators();
      this.setAddressValidators(false);
    } else {
      deliveryGroup.controls['deliveryMethod'].setValidators([Validators.required]);
      emailControl.setValidators(this.showInterestInvoiceEmail ? [Validators.email, Validators.required] : [Validators.email]);
      eInvoiceAddressControl.setValidators(this.showInterestInvoiceEInvoiceAddress ? [Validators.required, this.eInvoiceAddressValidator] : [this.eInvoiceAddressValidator]);
      this.setAddressValidators(this.showInterestInvoicePostalAddress);
    }

    deliveryGroup.controls['deliveryMethod'].updateValueAndValidity({ emitEvent: false });
    emailControl.updateValueAndValidity({ emitEvent: false });
    eInvoiceAddressControl.updateValueAndValidity({ emitEvent: false });

    Object.values(addressGroup.controls).forEach(control => control.updateValueAndValidity({ emitEvent: false }));
  }

  private setAddressValidators(required: boolean): void {
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['name'], required);
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['street'], required);
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['postalCode'], required);
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['city'], required);
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['country'], required);
  }

  private setRequiredValidator(control: AbstractControl, required: boolean): void {
    if ('required' in (control as any)) {
      (control as any).required = required;
    }

    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
  }

  private eInvoiceAddressValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    return /^([^:]+:[^:]+)$/.test(control.value) ? null : { eInvoiceAddress: { message: 'ERROR.FORMAT' } };
  }

  private resolveDefaultArticleId(): string {
    const defaultArticleId = this.preview?.defaultArticleId;
    if (!defaultArticleId) {
      return '';
    }

    return this.articleSelection.some(item => item.value === defaultArticleId) ? defaultArticleId : '';
  }

  private resolveDefaultLeaseAgreementId(): string {
    const currentLeaseAgreements = this.availableLeaseAgreements.filter(
      leaseAgreement => leaseAgreement.status === LeaseAgreementStatus.Valid
    );
    if (!currentLeaseAgreements.length) {
      return '';
    }

    return currentLeaseAgreements
      .slice()
      .sort((left, right) => this.getLeaseAgreementDefaultSortOrder(left) - this.getLeaseAgreementDefaultSortOrder(right))[0].id;
  }

  private getLeaseAgreementDefaultSortOrder(leaseAgreement: AmortizationPlanLeaseAgreementOption): number {
    return leaseAgreement.category === LeaseAgreementCategory.Dwelling ? 0 : 1;
  }

  private toLeaseAgreementSelection(): SelectionItem<string>[] {
    const items = this.availableLeaseAgreements.map(leaseAgreement => new SelectionItem(leaseAgreement.displayName, leaseAgreement.id));
    return [new SelectionItem('', ''), ...items];
  }

  private toFirstRentPeriodSelection(): SelectionItem<string>[] {
    const items = this.availableFirstRentPeriods.map(rentPeriod => new SelectionItem(rentPeriod.displayName, rentPeriod.id));
    return [new SelectionItem('', ''), ...items];
  }

  private toArticleSelection(articles: Article[], preview: AmortizationPlanCreatePreview): SelectionItem<string>[] {
    const currencyCode = preview?.totalAmount?.currencyCode;
    const selection = (articles ?? [])
      .filter(article => !currencyCode || !article.currencyCode || article.currencyCode === currencyCode)
      .sort((left, right) => left.toString().localeCompare(right.toString()))
      .map(article => new SelectionItem(article.toString(), article.id));

    return [new SelectionItem('', ''), ...selection];
  }

  get installmentPreview(): AmortizationPlanInstallmentEdit[] {
    return this.createInstallments();
  }

  private createInstallments(): AmortizationPlanInstallmentEdit[] {
    const totalAmount = this.selectedTotalAmount;
    const paymentCount = Number(this.form.controls['paymentCount'].value ?? 0);

    if (!paymentCount) {
      return [];
    }

    const baseAmount = paymentCount > 1 ? Money.RoundByCurrency(totalAmount.amount / paymentCount, totalAmount.currencyCode) : totalAmount.amount;
    let remainingAmount = totalAmount.amount;

    if (this.selectedLeaseAgreement) {
      const selectedIndex = this.availableFirstRentPeriods.findIndex(item => item.id === this.selectedFirstRentPeriod?.id);
      if (selectedIndex < 0) {
        return [];
      }

      const selectedRentPeriods = this.availableFirstRentPeriods.slice(selectedIndex, selectedIndex + paymentCount);
      return Array.from({ length: paymentCount }, (_, index) => {
        const amount = index === paymentCount - 1 ? Money.RoundByCurrency(remainingAmount, totalAmount.currencyCode) : baseAmount;
        remainingAmount = Money.RoundByCurrency(remainingAmount - amount, totalAmount.currencyCode);
        const rentPeriod = selectedRentPeriods[index];

        return {
          dueDate: rentPeriod?.dueDate?.clone() ?? this.getContractInstallmentDueDate(index, selectedRentPeriods),
          amount,
          paidAmount: 0,
        };
      });
    }

    const firstDueDate = LocalDate.fromDate(this.form.controls['firstDueDate'].value);
    if (!firstDueDate?.hasValue()) {
      return [];
    }

    return Array.from({ length: paymentCount }, (_, index) => {
      const dueDate = firstDueDate.clone();
      if (index > 0) {
        dueDate.addMonths(index);
      }

      const amount = index === paymentCount - 1 ? Money.RoundByCurrency(remainingAmount, totalAmount.currencyCode) : baseAmount;
      remainingAmount = Money.RoundByCurrency(remainingAmount - amount, totalAmount.currencyCode);

      return {
        dueDate,
        amount,
        paidAmount: 0,
      };
    });
  }

  private getContractInstallmentDueDate(index: number, selectedRentPeriods: AmortizationPlanRentPeriodOption[]): LocalDate {
    const lastRentPeriod = selectedRentPeriods[selectedRentPeriods.length - 1] ?? this.selectedFirstRentPeriod;
    const dueDate = lastRentPeriod?.dueDate?.clone();
    if (!dueDate) {
      return null;
    }

    const invoicePeriodMonths = lastRentPeriod.invoicePeriodMonths || this.selectedLeaseAgreement?.firstRentPeriods?.[0]?.invoicePeriodMonths || 1;
    dueDate.addMonths((index - selectedRentPeriods.length + 1) * invoicePeriodMonths);
    return dueDate;
  }

  private getDefaultFirstDueDate(): LocalDate {
    const nextMonth = LocalDate.today();
    nextMonth.addMonths(1);
    return LocalDate.fromDate(nextMonth.lastDayInMonth());
  }

  private toCreateRequest(): AmortizationPlanCreateRequest {
    return {
      invoiceIds: this.selectedEligibleInvoices.map(invoice => invoice.invoiceId),
      debtorContactId: this.preview?.debtorContactId,
      articleId: this.form.controls['articleId'].value,
      leaseAgreementId: this.form.controls['leaseAgreementId'].value || null,
      separateInvoicing: !this.form.controls['leaseAgreementId'].value,
      firstRentPeriodId: this.form.controls['firstRentPeriodId'].value || null,
      interestRate: this.form.controls['interestRate'].value || 0,
      totalAmount: this.selectedTotalAmount,
      interestInvoiceDelivery: {
        deliveryMethod: this.interestInvoiceDeliveryGroup.controls['deliveryMethod'].value,
        email: this.interestInvoiceDeliveryGroup.controls['email'].value,
        eInvoiceAddress: this.interestInvoiceDeliveryGroup.controls['eInvoiceAddress'].value,
        address: {
          name: this.interestInvoiceDeliveryAddressGroup.controls['name'].value,
          additionalName: this.interestInvoiceDeliveryAddressGroup.controls['additionalName'].value,
          street: this.interestInvoiceDeliveryAddressGroup.controls['street'].value,
          postalCode: this.interestInvoiceDeliveryAddressGroup.controls['postalCode'].value,
          city: this.interestInvoiceDeliveryAddressGroup.controls['city'].value,
          country: this.interestInvoiceDeliveryAddressGroup.controls['country'].value,
          countryCode: this.interestInvoiceDeliveryAddressGroup.controls['country'].value,
        },
      },
      note: this.form.controls['note'].value,
      installments: this.createInstallments(),
    };
  }
}
